import type { CSSProperties } from "react";
import { useEffect, useState } from "react";
import type { PerpLeg } from "../lib/composePayoff";
import type { SelectedPerp } from "../types";
import { PerpSimulateForm } from "./perp-simulate-form";

function Chevron({ open, color }: { open: boolean; color: string }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      aria-hidden
      style={{
        transform: open ? "rotate(180deg)" : "rotate(0deg)",
        transition: "transform 180ms ease",
        color,
      }}
    >
      <path
        d="M2 4.5 L6 8.5 L10 4.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const PERP_COLOR = "#a78bfa";
/** Hyperliquid brand — mint green from their icon-mintgreen asset. */
const HL_MINT = "#97FCE4";

const monoLabel: CSSProperties = {
  fontFamily: "var(--f-mono)",
  fontSize: 9,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  color: "var(--fg-meta)",
};

const monoValue: CSSProperties = {
  fontFamily: "var(--f-mono)",
  fontSize: 12,
  color: "var(--fg-hi)",
  fontVariantNumeric: "tabular-nums",
};

const tabBtn = (active: boolean): CSSProperties => ({
  flex: 1,
  padding: "8px 10px",
  background: "transparent",
  border: "none",
  borderBottom: active ? `1px solid ${PERP_COLOR}` : "1px solid transparent",
  color: active ? PERP_COLOR : "var(--fg-dim)",
  fontFamily: "var(--f-mono)",
  fontSize: 10,
  letterSpacing: "0.16em",
  fontWeight: 500,
  textTransform: "uppercase",
  cursor: "pointer",
});

export type PerpPanelMode = "existing" | "simulate";

function formatPx(n: number): string {
  if (!isFinite(n)) return "—";
  if (n >= 1000) return n.toFixed(2);
  if (n >= 1) return n.toFixed(4);
  return n.toPrecision(4);
}

function formatUsd(n: number): string {
  if (!isFinite(n) || n === 0) return "—";
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(2)}B`;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1000) return `$${(n / 1000).toFixed(1)}K`;
  return `$${n.toFixed(2)}`;
}

function MetricChip({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        padding: "6px 8px",
        border: "1px solid var(--line-1)",
        background: "var(--bg-0)",
        borderRadius: 2,
        minWidth: 0,
      }}
    >
      <span style={{ ...monoLabel, fontSize: 8, letterSpacing: "0.14em" }}>
        {label}
      </span>
      <span
        style={{
          ...monoValue,
          fontSize: 11,
          color: color ?? "var(--fg-hi)",
        }}
      >
        {value}
      </span>
    </div>
  );
}

export function PerpsPanel({
  selected,
  leg,
  mode,
  onModeChange,
  onLegChange,
  onSimulateInit,
  onExplore,
}: {
  selected: SelectedPerp | null;
  leg: PerpLeg | null;
  mode: PerpPanelMode;
  onModeChange: (mode: PerpPanelMode) => void;
  onLegChange: (leg: PerpLeg) => void;
  onSimulateInit: () => void;
  onExplore: () => void;
}) {
  if (!selected) {
    return (
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 14,
          padding: 24,
        }}
      >
        <div style={{ ...monoLabel, color: "var(--fg-dim)", textAlign: "center" }}>
          NO PERP SELECTED
        </div>
        <button
          type="button"
          onClick={onExplore}
          style={{
            fontFamily: "var(--f-mono)",
            fontSize: 10,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            fontWeight: 500,
            color: PERP_COLOR,
            background: "transparent",
            border: `1px solid ${PERP_COLOR}`,
            padding: "6px 14px",
            cursor: "pointer",
            borderRadius: 2,
          }}
        >
          BROWSE MARKETS →
        </button>
      </div>
    );
  }

  const chgColor = selected.dayChangePct >= 0 ? "var(--up)" : "var(--dn)";

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* HERO — subtle Hyperliquid mint gradient card */}
      <div
        style={{
          position: "relative",
          margin: 10,
          padding: "12px 14px 14px",
          minHeight: 116,
          display: "flex",
          flexDirection: "column",
          gap: 10,
          justifyContent: "space-between",
          borderRadius: 3,
          background: `
            linear-gradient(135deg, ${HL_MINT}0F 0%, transparent 55%),
            linear-gradient(180deg, var(--bg-2) 0%, var(--bg-1) 100%)
          `,
          border: `1px solid ${HL_MINT}22`,
          boxShadow: `inset 0 0 0 1px rgba(255,255,255,0.02), 0 1px 0 rgba(0,0,0,0.3)`,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 8,
          }}
        >
          <img
            src="/hyperliquid/icon/icon-mintgreen.svg"
            alt=""
            style={{ width: 14, height: 14, flexShrink: 0, alignSelf: "center" }}
          />
          <div
            style={{
              fontFamily: "var(--f-mono)",
              fontSize: 14,
              fontWeight: 600,
              letterSpacing: "0.04em",
              color: "var(--fg-max)",
            }}
          >
            {selected.coin}
          </div>
          <span
            style={{
              ...monoLabel,
              fontSize: 8,
              color: HL_MINT,
              border: `1px solid ${HL_MINT}55`,
              background: `${HL_MINT}10`,
              padding: "1px 6px",
              borderRadius: 2,
              marginLeft: 4,
            }}
          >
            {selected.maxLeverage}× MAX
          </span>
          <div style={{ marginLeft: "auto", ...monoValue, fontSize: 14 }}>
            {formatPx(selected.markPx)}
          </div>
          <div
            style={{
              ...monoValue,
              color: chgColor,
              fontSize: 11,
              minWidth: 58,
              textAlign: "right",
            }}
          >
            {selected.dayChangePct >= 0 ? "+" : ""}
            {selected.dayChangePct.toFixed(2)}%
          </div>
        </div>

        {/* METRIC STRIP */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 4,
          }}
        >
          <MetricChip label="24H VOL" value={formatUsd(selected.dayNtlVlm)} />
          <MetricChip label="OPEN INT" value={formatUsd(selected.openInterest)} />
          <MetricChip
            label="FUNDING 1H"
            value={`${selected.fundingPct >= 0 ? "+" : ""}${selected.fundingPct.toFixed(3)}%`}
            color={selected.fundingPct >= 0 ? "var(--up)" : "var(--dn)"}
          />
        </div>
      </div>

      {/* TABS */}
      <div style={{ display: "flex", borderBottom: "1px solid var(--line-2)" }}>
        <button
          type="button"
          style={tabBtn(mode === "existing")}
          onClick={() => onModeChange("existing")}
        >
          EXISTING
        </button>
        <button
          type="button"
          style={tabBtn(mode === "simulate")}
          onClick={() => {
            onModeChange("simulate");
            if (!leg) onSimulateInit();
          }}
        >
          SIMULATE
        </button>
      </div>

      {/* BODY */}
      <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        {mode === "existing" ? (
          <div
            style={{
              minHeight: 160,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 12,
              padding: 24,
              textAlign: "center",
            }}
          >
            <div style={{ ...monoLabel, color: "var(--fg-dim)" }}>
              NO OPEN POSITIONS ON {selected.coin}
            </div>
            <button
              type="button"
              onClick={() => {
                onModeChange("simulate");
                if (!leg) onSimulateInit();
              }}
              style={{
                fontFamily: "var(--f-mono)",
                fontSize: 10,
                letterSpacing: "0.16em",
                fontWeight: 500,
                textTransform: "uppercase",
                color: PERP_COLOR,
                background: `${PERP_COLOR}10`,
                border: `1px solid ${PERP_COLOR}`,
                padding: "8px 18px",
                cursor: "pointer",
                borderRadius: 2,
              }}
            >
              SIMULATE POSITION →
            </button>
          </div>
        ) : leg ? (
          <SimulateExtension
            leg={leg}
            market={selected}
            onChange={onLegChange}
          />
        ) : null}
      </div>
    </div>
  );
}

function SimulateExtension({
  leg,
  market,
  onChange,
}: {
  leg: PerpLeg;
  market: SelectedPerp;
  onChange: (next: PerpLeg) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  // Reset to closed whenever the market flips.
  useEffect(() => setExpanded(false), [market.coin]);
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 14px",
          background: "transparent",
          border: "none",
          borderBottom: expanded ? "1px solid var(--line-2)" : undefined,
          color: expanded ? PERP_COLOR : "var(--fg-meta)",
          fontFamily: "var(--f-mono)",
          fontSize: 10,
          letterSpacing: "0.16em",
          fontWeight: 500,
          textTransform: "uppercase",
          cursor: "pointer",
          boxShadow: `inset 2px 0 0 ${PERP_COLOR}`,
          transition: "color 160ms ease",
        }}
        aria-expanded={expanded}
      >
        <span>Simulated Position</span>
        <Chevron open={expanded} color="currentColor" />
      </button>
      {expanded && (
        <PerpSimulateForm market={market} leg={leg} onChange={onChange} />
      )}
    </div>
  );
}
